"use client";

import { useState } from "react";
import Icon from "@/components/ui/Icon/Icon";
import Button from "@/components/ui/Button/Button";
import { withBasePath } from "@/lib/basePath";
import {
  networkCategories,
  networkStats,
  networkFeatures,
  volumeLegend,
  featuredFarm,
} from "@/lib/mockData";

export default function NetworkMap() {
  const [activeCat, setActiveCat] = useState("all");

  return (
    <section className="net-map">
      <img
        className="net-map--bg"
        src={withBasePath("/images/main_section_map/main_section_map.jpg")}
        alt="Карта фермерских хозяйств Молдовы"
      />
      <div className="net-map--scrim" />

      <div className="net-map--inner">
        {/* Левая панель */}
        <div className="net-map--panel">
          <p className="net-map--label">Network</p>
          <h2 className="net-map--title">
            Фермерские хозяйства
            <br />
            Молдовы
          </h2>
          <p className="net-map--desc">
            Исследуйте предложения фермеров по всей стране и находите надёжных
            поставщиков качественной продукции.
          </p>

          <label className="net-map--search">
            <Icon name="search" size={18} className="net-map--search-icon" />
            <input
              className="net-map--search-input"
              type="text"
              placeholder="Поиск по хозяйствам, регионам..."
            />
          </label>

          <p className="net-map--cats-title">Категории продукции</p>
          <div className="net-map--cats">
            {networkCategories.map((cat) => (
              <button
                key={cat.id}
                className={`net-map--cat${activeCat === cat.id ? " is-active" : ""}`}
                onClick={() => setActiveCat(cat.id)}
              >
                <span
                  className="net-map--cat-icon"
                  style={{ background: `${cat.color}26` }}
                >
                  {cat.emoji}
                </span>
                <span className="net-map--cat-name">{cat.name}</span>
                <span className="net-map--cat-count">{cat.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Верхние плашки статистики */}
        <div className="net-map--stats">
          {networkStats.map((stat) => (
            <div key={stat.label} className="net-map--stat">
              <span className="net-map--stat-icon">
                <Icon name={stat.icon} size={22} />
              </span>
              <span className="net-map--stat-body">
                <strong className="net-map--stat-value">{stat.value}</strong>
                <span className="net-map--stat-label">{stat.label}</span>
              </span>
              {stat.link && (
                <Icon
                  name="chevron-right"
                  size={16}
                  className="net-map--stat-arrow"
                />
              )}
            </div>
          ))}
        </div>

        {/* Карточка фермера */}
        <article className="farm-card">
          <div className="farm-card--head">
            <img
              className="farm-card--photo"
              src={featuredFarm.image}
              alt={featuredFarm.name}
            />
            <div className="farm-card--head-body">
              <div className="farm-card--name-row">
                <h3 className="farm-card--name">{featuredFarm.name}</h3>
                {featuredFarm.verified && (
                  <Icon
                    name="check-circle"
                    size={18}
                    className="farm-card--verified"
                  />
                )}
              </div>
              <p className="farm-card--location">{featuredFarm.location}</p>
              <div className="farm-card--rating">
                <Icon name="star-fill" size={16} className="farm-card--star" />
                <strong>{featuredFarm.rating}</strong>
                <span className="farm-card--reviews">
                  ({featuredFarm.reviews} отзыва)
                </span>
              </div>
            </div>
            <button
              className="farm-card--close"
              type="button"
              aria-label="Закрыть карточку"
            >
              <Icon name="x" size={18} />
            </button>
          </div>

          <div className="farm-card--info">
            {featuredFarm.info.map((chip) => (
              <div key={chip.label} className="farm-card--chip">
                <Icon name={chip.icon} size={18} className="farm-card--chip-icon" />
                <span className="farm-card--chip-body">
                  <span className="farm-card--chip-label">{chip.label}</span>
                  <strong className="farm-card--chip-value">{chip.value}</strong>
                </span>
              </div>
            ))}
          </div>

          <p className="farm-card--subtitle">Что выращивается</p>
          <div className="farm-card--crops">
            {featuredFarm.crops.map((crop) => (
              <div key={crop.name} className="farm-card--crop">
                <span className="farm-card--crop-emoji">{crop.emoji}</span>
                <strong className="farm-card--crop-name">{crop.name}</strong>
                <span className="farm-card--crop-volume">{crop.volume}</span>
              </div>
            ))}
            <div className="farm-card--crop is-more">
              <strong className="farm-card--crop-name">
                +{featuredFarm.cropsMore}
              </strong>
              <span className="farm-card--crop-volume">ещё</span>
            </div>
          </div>

          <div className="farm-card--stats">
            {featuredFarm.stats.map((stat) => (
              <div key={stat.label} className="farm-card--stat">
                <Icon name={stat.icon} size={20} className="farm-card--stat-icon" />
                <span className="farm-card--stat-body">
                  <strong className="farm-card--stat-value">{stat.value}</strong>
                  <span className="farm-card--stat-label">{stat.label}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="farm-card--actions">
            <Button variant="outline" className="farm-card--btn">
              Смотреть профиль
            </Button>
            <Button variant="primary" className="farm-card--btn">
              Связаться с фермером
              <Icon name="arrow-right" size={18} />
            </Button>
          </div>
        </article>

        {/* Контролы карты */}
        <div className="net-map--controls">
          <button className="net-map--ctrl" type="button" aria-label="Моё местоположение">
            <Icon name="locate" size={20} />
          </button>
          <div className="net-map--ctrl-zoom">
            <button className="net-map--ctrl" type="button" aria-label="Приблизить">
              <Icon name="plus" size={20} />
            </button>
            <button className="net-map--ctrl" type="button" aria-label="Отдалить">
              <Icon name="minus" size={20} />
            </button>
          </div>
          <button className="net-map--ctrl" type="button" aria-label="Сбросить вид">
            <Icon name="navigation" size={18} />
          </button>
        </div>
      </div>

      {/* Нижняя плашка */}
      <div className="net-map--footer">
        <div className="net-map--features">
          {networkFeatures.map((feature) => (
            <div key={feature.title} className="net-map--feature">
              <span className="net-map--feature-icon">
                <Icon name={feature.icon} size={20} />
              </span>
              <span className="net-map--feature-body">
                <strong className="net-map--feature-title">{feature.title}</strong>
                <span className="net-map--feature-text">{feature.text}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="net-map--legend">
          <p className="net-map--legend-title">Объём производства</p>
          <div className="net-map--legend-items">
            {volumeLegend.map((vol) => (
              <span key={vol.label} className="net-map--legend-item">
                <span
                  className="net-map--legend-dot"
                  style={{ background: vol.color }}
                />
                {vol.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
